import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Box from "common/basicComponents/Box";
import Accordion from "common/basicComponents/Accordion";
import Button, { ButtonVariant, ButtonType } from "common/basicComponents/Button";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";
import { IconName } from "common/basicComponents/Icon";
import FormInputField, {
  FormInputFieldType,
  NumberType,
} from "common/formFields/FormInputField";
import FormSelectField from "common/formFields/FormSelectField";
import FormCheckboxField from "common/formFields/FormCheckboxField";
import FormDateField from "common/formFields/FormDateField";
import { useViewport, ViewportType } from "utils/ViewportContext";
import { formatDateDMY, formatUnixDMY } from "utils/DateTimeUtils";
import {
  addNotification,
  NotificationVariant,
} from "components/notifications/NotificationSlice";
import {
  getSubscriptionConfig,
  saveSubscriptionConfig,
  resumeSubscription,
  pauseSubscription,
  unpauseSubscription,
  addOneTimeCharge,
  getOneTimeCharges,
  deleteOneTimeCharge,
  getUpcomingInvoice,
} from "components/billing/BillingActions";
import EditOneTimeChargeDialog from "./EditOneTimeChargeDialog";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import UnpauseSubscriptionDialog from "./UnpauseSubscriptionDialog";

const Content = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 2rem 0.625rem;
  display: flex;
  flex-wrap: wrap;
  row-gap: 1.25rem;
  align-items: flex-end;
`;

const ButtonRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.625rem 0.625rem 0;
`;

const PendingRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0.625rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const getWidth = (viewport) =>
  ({
    [ViewportType.DESKTOP]: "33%",
    [ViewportType.TABLET]: "50%",
    [ViewportType.MOBILE]: "100%",
  }[viewport]);

const CURRENCIES = ["eur", "pln", "usd", "gbp"];
const INTERVALS = ["day", "week", "month", "year"];
const START_MODES = ["scheduled", "immediate_prorated", "immediate_now"];
const DEAD_STATUSES = ["canceled", "incomplete_expired"];

// Default scheduled start = the next 1st of the month (matches backend).
const defaultScheduledStartIso = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  ).toISOString();
};

const fmtIso = formatDateDMY;
const fmtUnix = formatUnixDMY;

const currencyOption = (c) => ({ value: c, label: (c || "").toUpperCase() });
const intervalOption = (i, t) => ({ value: i, label: t(`interval_${i}`) });
const startModeOption = (m, t) => ({ value: m, label: t(`startMode_${m}`) });

const startModeHintKey = (values) => {
  const mode = values.startMode?.value;
  if (mode === "scheduled") return "startModeHint_scheduled";
  if (mode === "immediate_now") return "startModeHint_immediate_now";
  return values.prorateFirstPeriod
    ? "startModeHint_immediate_prorated_prorated"
    : "startModeHint_immediate_prorated_full";
};

const configToValues = (config, t) => ({
  amount: config?.amount_minor != null ? config.amount_minor / 100 : 200,
  currency: currencyOption(config?.currency || "eur"),
  intervalUnit: intervalOption(config?.interval_unit || "month", t),
  intervalCount: config?.interval_count || 1,
  startMode: startModeOption("scheduled", t),
  // Always default a NEW subscription's start to the fresh next-1st — never a
  // stale start_date left over from a previously-canceled subscription (which
  // could be in the past and would either mislead the picker or 422 on Start).
  startDate: defaultScheduledStartIso(),
  prorateFirstPeriod: false,
  oneTimeAmount: "",
  oneTimeDescription: "",
});

const ValidationSchema = Yup.object({
  amount: Yup.number()
    .typeError("common:validator.required")
    .required("common:validator.required")
    .moreThan(0, "common:validator.required"),
  intervalCount: Yup.number()
    .typeError("common:validator.required")
    .required("common:validator.required")
    .min(1, "common:validator.required"),
});

const SubscriptionForm = () => {
  const { t } = useTranslation("billing");
  const { type: viewportType } = useViewport();
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [chargingOneTime, setChargingOneTime] = useState(false);
  const [pendingCharges, setPendingCharges] = useState([]);
  const [editingCharge, setEditingCharge] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [unpauseOpen, setUnpauseOpen] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [pausing, setPausing] = useState(false);
  // Stable key so a double-click / retry of "add one-off" doesn't create two
  // charges; regenerated after each successful add.
  const oneTimeKeyRef = useRef(crypto.randomUUID());

  const reload = () => getSubscriptionConfig().then((c) => setConfig(c));
  const reloadCharges = () =>
    getOneTimeCharges().then((items) => setPendingCharges(items));
  const reloadUpcoming = () =>
    getUpcomingInvoice().then((inv) => setUpcoming(inv));

  useEffect(() => {
    reload();
    reloadCharges();
    reloadUpcoming();
  }, []);

  const live = config?.stripeStatus;
  const hasLiveSub = !!(
    config?.stripe_subscription_id &&
    live &&
    !DEAD_STATUSES.includes(live.status)
  );
  const paused = !!(hasLiveSub && live.paused);
  const scheduledToCancel = !!(
    hasLiveSub &&
    !paused &&
    live.cancel_at_period_end
  );
  const active = hasLiveSub && !paused && !scheduledToCancel;
  const liveStatus = live?.status || config?.status;

  const buildConfigPayload = (values, enabled, immediate) => ({
    enabled,
    amount_minor: Math.round(Number(values.amount) * 100),
    currency: values.currency?.value ?? values.currency,
    interval_unit: values.intervalUnit?.value ?? values.intervalUnit,
    interval_count: Number(values.intervalCount),
    start_date: values.startDate || null,
    start_mode: values.startMode?.value ?? values.startMode,
    prorate_first_period: !!values.prorateFirstPeriod,
    ...(immediate != null ? { immediate } : {}),
  });

  const onSubmit = async (values, form) => {
    form.setSubmitting(true);
    const ok = await saveSubscriptionConfig(buildConfigPayload(values, true));
    form.setSubmitting(false);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("common:form.savedSuccessfully"),
        })
      );
      reload();
      reloadUpcoming();
    }
  };

  const handleCancel = async (immediate, values) => {
    const ok = await saveSubscriptionConfig(
      buildConfigPayload(values, false, immediate)
    );
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t(
            immediate ? "subscriptionCanceled" : "subscriptionCancelScheduled"
          ),
        })
      );
      setCancelOpen(false);
      reload();
      reloadUpcoming();
    }
    return ok;
  };

  const handleResume = async () => {
    setResuming(true);
    const ok = await resumeSubscription();
    setResuming(false);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("subscriptionResumed"),
        })
      );
      reload();
      reloadUpcoming();
    }
  };

  const handlePause = async () => {
    setPausing(true);
    const ok = await pauseSubscription();
    setPausing(false);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("subscriptionPausedMsg"),
        })
      );
      reload();
      reloadUpcoming();
    }
  };

  const handleUnpause = async (voidDrafts) => {
    const ok = await unpauseSubscription(voidDrafts);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("subscriptionResumed"),
        })
      );
      setUnpauseOpen(false);
      reload();
      reloadUpcoming();
    }
    return ok;
  };

  const handleDeleteCharge = async (id) => {
    const ok = await deleteOneTimeCharge(id);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("oneTimeChargeDeleted"),
        })
      );
      reloadCharges();
      reloadUpcoming();
    }
  };

  const handleOneTimeCharge = async (values, setFieldValue) => {
    const amount = Number(values.oneTimeAmount);
    if (!amount || amount <= 0) {
      dispatch(
        addNotification({
          variant: NotificationVariant.ERROR,
          message: t("oneTimeAmountRequired"),
        })
      );
      return;
    }
    setChargingOneTime(true);
    const ok = await addOneTimeCharge({
      amount_minor: Math.round(amount * 100),
      description: values.oneTimeDescription || undefined,
      idempotency_key: oneTimeKeyRef.current,
    });
    setChargingOneTime(false);
    if (ok) {
      oneTimeKeyRef.current = crypto.randomUUID();
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("oneTimeChargeAdded"),
        })
      );
      setFieldValue("oneTimeAmount", "");
      setFieldValue("oneTimeDescription", "");
      reloadCharges();
      reloadUpcoming();
    }
  };

  return (
    <Accordion text={t("subscriptionConfig")}>
      <EditOneTimeChargeDialog
        open={!!editingCharge}
        charge={editingCharge}
        onClose={() => setEditingCharge(null)}
        onSaved={() => {
          reloadCharges();
          reloadUpcoming();
        }}
      />
      <Formik
        initialValues={configToValues(config, t)}
        validationSchema={ValidationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Content>
            <CancelSubscriptionDialog
              open={cancelOpen}
              periodEnd={live?.current_period_end}
              onClose={() => setCancelOpen(false)}
              onConfirm={(immediate) => handleCancel(immediate, values)}
            />
            <UnpauseSubscriptionDialog
              open={unpauseOpen}
              onClose={() => setUnpauseOpen(false)}
              onConfirm={handleUnpause}
            />

            {liveStatus && (
              <Box width="100%" padding="0 0.625rem">
                <Typography
                  variant={TypographyVariant.CAPTION}
                  text={`${t("status")}: ${t(`status_${liveStatus}`, {
                    defaultValue: liveStatus,
                  })}`}
                />
              </Box>
            )}

            {scheduledToCancel && (
              <Box width="100%" padding="0 0.625rem">
                <Typography
                  variant={TypographyVariant.BODY1}
                  weight={TypographyWeight.BOLD}
                  text={t("scheduledCancel", {
                    date: fmtUnix(live.current_period_end),
                  })}
                />
              </Box>
            )}

            {paused && (
              <Box width="100%" padding="0 0.625rem">
                <Typography
                  variant={TypographyVariant.BODY1}
                  weight={TypographyWeight.BOLD}
                  text={t("subscriptionPausedBanner")}
                />
              </Box>
            )}

            <Box width={getWidth(viewportType)} padding="0 0.625rem">
              <FormInputField
                type={FormInputFieldType.NUMBER}
                numberCheck={NumberType.NON_NEGATIVE_DECIMAL}
                name="amount"
                id="amount"
                label={t("amount")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width={getWidth(viewportType)} padding="0 0.625rem">
              <FormSelectField
                name="currency"
                id="currency"
                label={t("currency")}
                options={CURRENCIES.map(currencyOption)}
              />
            </Box>
            <Box width={getWidth(viewportType)} padding="0 0.625rem">
              <FormSelectField
                name="intervalUnit"
                id="intervalUnit"
                label={t("intervalUnit")}
                options={INTERVALS.map((i) => intervalOption(i, t))}
              />
            </Box>
            <Box width={getWidth(viewportType)} padding="0 0.625rem">
              <FormInputField
                type={FormInputFieldType.NUMBER}
                numberCheck={NumberType.POSITIVE_INTEGER}
                name="intervalCount"
                id="intervalCount"
                label={t("intervalCount")}
                caption={t("intervalCountHint")}
                disabled={isSubmitting}
              />
            </Box>
            {!hasLiveSub && (
              <Box width={getWidth(viewportType)} padding="0 0.625rem">
                <FormSelectField
                  name="startMode"
                  id="startMode"
                  label={t("startMode")}
                  options={START_MODES.map((m) => startModeOption(m, t))}
                />
              </Box>
            )}
            {!hasLiveSub && values.startMode?.value === "scheduled" && (
              <Box width={getWidth(viewportType)} padding="0 0.625rem">
                <FormDateField
                  name="startDate"
                  id="startDate"
                  label={t("startDate")}
                  disablePastDates
                />
              </Box>
            )}
            {!hasLiveSub &&
              values.startMode?.value === "immediate_prorated" && (
                <Box width="100%" padding="0 0.625rem">
                  <FormCheckboxField
                    id="prorateFirstPeriod"
                    label={t("prorateFirstPeriod")}
                    description={t("prorateFirstPeriodHint", {
                      amount: `${values.amount || 0} ${(
                        values.currency?.value || "eur"
                      ).toUpperCase()}`,
                    })}
                  />
                </Box>
              )}
            {!hasLiveSub && (
              <Box width="100%" padding="0 0.625rem 0.5rem">
                <Typography
                  variant={TypographyVariant.CAPTION}
                  text={t(startModeHintKey(values), {
                    amount: `${values.amount || 0} ${(
                      values.currency?.value || "eur"
                    ).toUpperCase()}`,
                  })}
                />
              </Box>
            )}

            {hasLiveSub && (
              <Box width="100%" padding="0 0.625rem">
                <Typography
                  variant={TypographyVariant.CAPTION}
                  text={t("currencyChangeHint")}
                />
              </Box>
            )}

            <ButtonRow>
              {!hasLiveSub && (
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.GREEN}
                  text={t("startSubscription")}
                  minWidth="13rem"
                  onClick={submitForm}
                  isLoading={isSubmitting}
                />
              )}
              {hasLiveSub && (
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.GREEN}
                  text={t("saveChanges")}
                  minWidth="13rem"
                  onClick={submitForm}
                  isLoading={isSubmitting}
                />
              )}
              {active && (
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.OUTLINED}
                  text={t("pauseSubscription")}
                  minWidth="13rem"
                  onClick={handlePause}
                  isLoading={pausing}
                />
              )}
              {scheduledToCancel && (
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.OUTLINED}
                  text={t("resumeSubscription")}
                  minWidth="13rem"
                  onClick={handleResume}
                  isLoading={resuming}
                />
              )}
              {paused && (
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.OUTLINED}
                  text={t("resumeSubscription")}
                  minWidth="13rem"
                  onClick={() => setUnpauseOpen(true)}
                />
              )}
              {hasLiveSub && (
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.ERROR}
                  text={t("cancelSubscription")}
                  minWidth="13rem"
                  onClick={() => setCancelOpen(true)}
                />
              )}
            </ButtonRow>

            {hasLiveSub && (
              <>
                <Box width="100%" padding="1.5rem 0.625rem 0">
                  <Typography
                    variant={TypographyVariant.H5}
                    text={t("oneTimeCharge")}
                  />
                </Box>
                <Box width="100%" padding="0 0.625rem">
                  <Typography
                    variant={TypographyVariant.CAPTION}
                    text={t("oneTimeChargeHint")}
                  />
                </Box>
                <Box width={getWidth(viewportType)} padding="0 0.625rem">
                  <FormInputField
                    type={FormInputFieldType.NUMBER}
                    numberCheck={NumberType.NON_NEGATIVE_DECIMAL}
                    name="oneTimeAmount"
                    id="oneTimeAmount"
                    label={t("oneTimeAmount")}
                    disabled={chargingOneTime}
                  />
                </Box>
                <Box width={getWidth(viewportType)} padding="0 0.625rem">
                  <FormInputField
                    type={FormInputFieldType.TEXT}
                    name="oneTimeDescription"
                    id="oneTimeDescription"
                    label={t("oneTimeDescription")}
                    disabled={chargingOneTime}
                  />
                </Box>
                <Box width="100%" padding="0.625rem 0.625rem 0">
                  <Button
                    type={ButtonType.BUTTON}
                    variant={ButtonVariant.OUTLINED}
                    text={t("addToNextInvoice")}
                    minWidth="13rem"
                    onClick={() => handleOneTimeCharge(values, setFieldValue)}
                    isLoading={chargingOneTime}
                  />
                </Box>

                <Box width="100%" padding="1rem 0.625rem 0">
                  <Typography
                    variant={TypographyVariant.CAPTION}
                    text={t("pendingOneTimeCharges")}
                  />
                </Box>
                {pendingCharges.length ? (
                  pendingCharges.map((charge) => (
                    <PendingRow key={charge.id}>
                      <Typography
                        variant={TypographyVariant.BODY2}
                        text={`${(charge.amount / 100).toFixed(2)} ${String(
                          charge.currency
                        ).toUpperCase()}${
                          charge.description ? ` — ${charge.description}` : ""
                        }`}
                      />
                      <RowActions>
                        <Button
                          type={ButtonType.BUTTON}
                          variant={ButtonVariant.OUTLINED}
                          icon={IconName.EDIT}
                          onClick={() => setEditingCharge(charge)}
                        />
                        <Button
                          type={ButtonType.BUTTON}
                          variant={ButtonVariant.ERROR}
                          icon={IconName.TRASH}
                          onClick={() => handleDeleteCharge(charge.id)}
                        />
                      </RowActions>
                    </PendingRow>
                  ))
                ) : (
                  <Box width="100%" padding="0 0.625rem">
                    <Typography
                      variant={TypographyVariant.CAPTION}
                      text={t("noPendingOneTimeCharges")}
                    />
                  </Box>
                )}

                {upcoming && (
                  <>
                    <Box width="100%" padding="1.5rem 0.625rem 0">
                      <Typography
                        variant={TypographyVariant.H5}
                        text={t("upcomingInvoice")}
                      />
                    </Box>
                    <Box width="100%" padding="0 0.625rem">
                      <Typography
                        variant={TypographyVariant.CAPTION}
                        text={`${t("upcomingChargeDate")}: ${fmtIso(
                          upcoming.charge_date
                        )}`}
                      />
                    </Box>
                    {(upcoming.lines || []).map((line, i) => (
                      <Box
                        key={`${i}-${line.amount}`}
                        width="100%"
                        padding="0 0.625rem"
                      >
                        <Typography
                          variant={TypographyVariant.BODY2}
                          text={`${line.description || t("subscription")}: ${(
                            line.amount / 100
                          ).toFixed(2)} ${String(
                            upcoming.currency
                          ).toUpperCase()}`}
                        />
                      </Box>
                    ))}
                    <Box width="100%" padding="0.25rem 0.625rem 0">
                      <Typography
                        variant={TypographyVariant.BODY1}
                        weight={TypographyWeight.BOLD}
                        text={`${t("upcomingTotal")}: ${(
                          upcoming.total / 100
                        ).toFixed(2)} ${String(
                          upcoming.currency
                        ).toUpperCase()}`}
                      />
                    </Box>
                  </>
                )}
              </>
            )}
          </Content>
        )}
      </Formik>
    </Accordion>
  );
};

export default SubscriptionForm;
